import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil, Trash2, Globe } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle } from '@/components/ui';
import { countryDisplayName } from '@/lib/data/iso-3166';
import type { MnoIpRangeEntry, MnoOperator } from '@/types/mno-ip-range';
import { mnoIpRangeKey } from '@/types/mno-ip-range';

interface MnoIpRangeListProps {
  entries: MnoIpRangeEntry[];
  operators: MnoOperator[];
  readOnly?: boolean;
  onEdit?: (operatorId: string, countryCode: string) => void;
  onDelete?: (operatorId: string, countryCode: string) => void;
}

/**
 * Liste der MNO-IP-Ranges, nach Land → Operator → Ranges gruppiert (Issue #318,
 * #326 echte Daten). Dieselbe Komponente bedient die Platform-Verwaltung (mit
 * Edit/Delete) und die PM-Lese-Ansicht (`readOnly`). Composite Key = operatorId +
 * country.code; pro CIDR wird die server-abgeleitete IP-Version angezeigt.
 */
export function MnoIpRangeList({
  entries,
  operators,
  readOnly,
  onEdit,
  onDelete,
}: MnoIpRangeListProps) {
  const { t, i18n } = useTranslation('mno-ip-ranges');
  const lang = i18n.language;

  const operatorName = (id: string) =>
    operators.find((op) => op.operatorId === id)?.name ?? id;

  const grouped = useMemo(() => {
    const nameOf = (id: string) =>
      operators.find((op) => op.operatorId === id)?.name ?? id;
    const byCountry = new Map<string, { name: string; list: MnoIpRangeEntry[] }>();
    for (const entry of entries) {
      const code = entry.country.code;
      const bucket = byCountry.get(code) ?? { name: entry.country.name, list: [] };
      bucket.list.push(entry);
      byCountry.set(code, bucket);
    }
    return [...byCountry.entries()]
      .map(([code, bucket]) => ({
        code,
        // Contract-Name bevorzugen (Spec 326), sonst ISO-Fallback.
        name: bucket.name || countryDisplayName(code, lang),
        list: [...bucket.list].sort((a, b) =>
          nameOf(a.operatorId).localeCompare(nameOf(b.operatorId), lang),
        ),
      }))
      .sort((a, b) => a.name.localeCompare(b.name, lang));
  }, [entries, operators, lang]);

  if (entries.length === 0) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-muted-foreground">
          {t('list.empty')}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {grouped.map(({ code, name, list }) => (
        <Card key={code}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Globe className="h-4 w-4 text-brand-accent" aria-hidden="true" />
              {name}
              <span className="text-sm font-normal text-muted-foreground">({code})</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {list.map((entry) => (
              <div
                key={mnoIpRangeKey(entry.operatorId, entry.country.code)}
                className="flex flex-col gap-2 rounded-md border p-3 md:flex-row md:items-start md:justify-between"
                data-testid="mno-ip-range-entry"
              >
                <div className="space-y-1.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{operatorName(entry.operatorId)}</span>
                    <Badge variant={entry.status === 'inactive' ? 'outline' : 'secondary'}>
                      {t(`status.${entry.status ?? 'active'}`)}
                    </Badge>
                    {entry.label && (
                      <span className="text-sm text-muted-foreground">{entry.label}</span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {entry.ipRanges.map((range) => (
                      <Badge key={range.cidr} variant="secondary" className="font-mono">
                        {range.cidr}
                        <span className="ml-1 text-[10px] uppercase opacity-70">{range.ipVersion}</span>
                      </Badge>
                    ))}
                  </div>
                  {entry.updatedBy && (
                    <p className="text-xs text-muted-foreground">
                      {t('list.updatedBy', {
                        by: entry.updatedBy,
                        at: entry.updatedAt ? new Date(entry.updatedAt).toLocaleDateString(lang) : '—',
                      })}
                    </p>
                  )}
                </div>

                {!readOnly && (
                  <div className="flex gap-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => onEdit?.(entry.operatorId, entry.country.code)}
                      aria-label={t('list.edit')}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          aria-label={t('list.delete')}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>{t('list.confirmDelete.title')}</AlertDialogTitle>
                          <AlertDialogDescription>
                            {t('list.confirmDelete.description')}
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>{t('list.confirmDelete.cancel')}</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => onDelete?.(entry.operatorId, entry.country.code)}
                          >
                            {t('list.confirmDelete.confirm')}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
